<template>
    <subscription-checkout-header v-model="donationDetails" />

    <Fluid v-if="!showStripeDetails&&!showPaypalDetails">
        <div class="field" v-if="isPayPalDonationsAvailable">
            <Button @click="usePaypalAction" class="justify-content-center">
                <i class="pi pi-paypal mr-2"></i>
                PayPal
            </Button>
        </div>

        <div class="field" v-if="donationDetails.stripe.apiKey">
            <Button @click="useStripeAction" class="justify-content-center">
                <i class="pi pi-credit-card mr-2"></i>
                Credit Card
            </Button>
        </div>
    </Fluid>

    <div v-if="showStripeDetails">
        <div class="field">
            <InputGroup>
                <InputGroupAddon>Email</InputGroupAddon>
                <InputText v-model="member.email"
                           placeholder="Your email address" dusk="email"
                           :invalid="v$.member.email.$invalid" />
            </InputGroup>
        </div>
        <div class="field">
            <InputGroup>
                <InputGroupAddon>Donation Amount</InputGroupAddon>
                <InputText v-model="donationDetails.amount" />
                <InputGroupAddon>{{ donationDetails.currency }}</InputGroupAddon>
            </InputGroup>
        </div>

        <div class="text-center">
            <Button v-if="!isBusy" @click="stripeCheckoutAction">CONTINUE</Button>
            <div v-if="isBusy">
                <Button :loading="true" outlined>PLEASE WAIT</Button>
            </div>
        </div>

        <stripe-checkout
            ref="checkoutRef"
            :pk="donationDetails.stripe.apiKey"
            :session-id="sessionId"
        />
    </div>

    <div v-if="showPaypalDetails&&donationDetails.paypal.clientId">
        <div class="field">
            <InputGroup>
                <InputGroupAddon>Donation Amount</InputGroupAddon>
                <InputText v-model="donationDetails.amount" />
                <InputGroupAddon>{{ donationDetails.currency }}</InputGroupAddon>
            </InputGroup>
        </div>

        <div>
            <div id="paypal-button-container"></div>
        </div>
    </div>
</template>
<script>
import { mapActions } from 'vuex'
import { subscriptionService } from "../../services";
import { StripeCheckout } from '@vue-stripe/vue-stripe';
import { loadScript } from '@paypal/paypal-js';
import subscriptionCheckoutHeader from "./CheckoutHeader.vue";
import {useVuelidate} from '@vuelidate/core'
import {required, email} from '@vuelidate/validators'

export default {
    setup() {
        return {v$: useVuelidate()}
    },
    props: {
        defaultPrice: {
            type: String,
            default: null,
        },
        customNote: {
            type: String,
            default: null,
        },
    },
    components: {
        subscriptionCheckoutHeader,
        StripeCheckout
    },
    data () {
        return {
            isBusy: false,
            member: {
                name: null,
                email: null
            },
            sessionId: null,
            showStripeDetails: false,
            showPaypalDetails: false
        }
    },
    computed: {
        donationDetails() {
            this.member = window.donationDetails.member
            return window.donationDetails;
        },
        isPayPalDonationsAvailable() {
            return this.donationDetails.paypal.paypalUrl || this.donationDetails.paypal.clientId
        }
    },
    created () {
        if (!this.donationDetails) {
            this.alertError('Wrong subscription URL');
        }
        if (this.defaultPrice) {
            window.donationDetails.amount = this.defaultPrice;
        }
        if (this.customNote) {
            window.donationDetails.customNote = this.customNote;
        }
    },
    mounted() {
    },
    methods: {
        ...mapActions({
            'alertSuccess': 'alert/success',
            'alertError': 'alert/error',
        }),
        useStripeAction() {
            this.showStripeDetails = true
            this.showPaypalDetails = false
        },
        usePaypalAction() {
            this.showStripeDetails = false
            this.showPaypalDetails = true

            if (this.donationDetails.paypal.clientId) {
                this.logDonationRequest();
                this.renderPaypalButton()
            } else {
                const openUrl = this.donationDetails.paypal.paypalUrl;
                // send open message if we are using embed page
                window.top.postMessage({'donateExternalUrl': openUrl, 'outpostOpenExternalUrl': openUrl}, "*");
                window.location.href = openUrl;
            }
        },
        stripeCheckoutAction() {
            this.v$.member.$touch()
            if (this.v$.member.$invalid) {
                event.preventDefault()
                return
            }

            this.isBusy = true
            subscriptionService.createDonationSession(this.donationDetails).then(
                response => {
                    this.isBusy = false

                    if (response.message) {
                        this.alertSuccess(response.message)

                        let portalUrl = this.donationDetails.portalUrl
                        if (portalUrl) {
                            setTimeout(function() {
                                window.location.href = portalUrl
                            }, 2000);
                        }
                    } else {
                        if (response.errorMessage) {
                            this.donationDetails.errorMessage = response.errorMessage
                        } else {
                            this.sessionId = response.sessionId
                            this.$refs.checkoutRef.redirectToCheckout();
                        }
                    }
                },
                error => {
                    this.alertError('Unable to process donation: ' + error)
                    this.isBusy = false
                }
            )
        },
        renderPaypalButton() {
            loadScript({ "client-id": this.donationDetails.paypal.clientId, 'enable-funding': 'card,credit' })
                .then((paypal) => {
                    paypal
                        .Buttons({
                            createOrder: (data, actions) => {
                                return actions.order.create({
                                    purchase_units: [{
                                        amount: {
                                            value: this.donationDetails.amount,
                                            currency_code: this.donationDetails.currency
                                        },
                                        description: 'Donation',
                                        custom_id: this.donationDetails.sessionHash,
                                    }]
                                });
                            },
                            onApprove: (data, actions) => {
                                const params = { ...this.donationDetails, 'orderId': data.orderID};

                                subscriptionService.capturePaypalOrder(params).then(
                                    response => {
                                        this.$router.push({'name': 'thank-you-donate',
                                            params: {sessionHash: this.donationDetails.sessionHash}
                                        });
                                    },
                                    error => {
                                        this.alertError('Unable to process donation: ' + error)
                                    }
                                )
                            }
                        })
                        .render("#paypal-button-container")
                        .catch((error) => {
                            console.error("failed to render the PayPal Buttons", error);
                        });
                })
                .catch((error) => {
                    console.error("failed to load the PayPal JS SDK script", error);
                });
        },
        logDonationRequest() {
            subscriptionService.createPaypalSession(this.donationDetails).then(
                response => {
                },
                error => {
                    this.alertError('Unable to create donation session: ' + error)
                }
            )
        }
    },
    validations: {
        member: {
            email: {
                required,
                email
            },
        }
    }

}
</script>
